<script lang="ts">
  import { fade } from 'svelte/transition';
  import CheckIcon from '$icons/CheckIcon.svelte';
  import { onMount } from 'svelte';
  import { goto } from '$app/navigation';
  import DefaultTag from '$lib/components/common/tagging/Tag.svelte';

  import type { FChallenge } from 'garden-common';
  import prettyMilliseconds from 'pretty-ms';

  import { isBefore } from 'garden-utils/index';
  import Media from '../../common/media/Media.svelte';

  export let variant: 'vertical' | 'horizontal' = 'horizontal';
  export let selectable = false;
  export let selected = false;
  export let containerCls = '';
  export let disableTransitions = false;

  export let challenge: FChallenge = null;

  // remove dupes reactively
  $: if (challenge) {
    challenge.challengePosts = [...new Set(challenge?.challengePosts)];
  }

  let displayGalleryCover = '';

  function determineGalleryCover() {
    if (!challenge || (challenge.challengePosts.length < 1 && !challenge.coverImageUrl)) return;

    if (challenge.coverImageUrl) {
      displayGalleryCover = challenge.coverImageUrl;
      return;
    }

    for (let i = 0; i < challenge.challengePosts.length; i++) {
      for (let j = 0; j < challenge.challengePosts[i].media?.length; j++) {
        if (
          challenge.challengePosts[i].media[j].thumbnails.length > 0 &&
          challenge.challengePosts[i].media[j].thumbnails[0]
        ) {
          displayGalleryCover = challenge.challengePosts[i].media[j].thumbnails[0].toString();
          return;
        }

        if (
          challenge.challengePosts[i].media[j].images.length > 0 &&
          challenge.challengePosts[i].media[j].images[0]
        ) {
          displayGalleryCover = challenge.challengePosts[i].media[j].images[0];
          return;
        }
      }
    }
  }
  let timeRemaining: string = '';
  function formatUTCDate(date: Date): string {
    return (
      date.getUTCDate() +
      '/' +
      (date.getUTCMonth() + 1) +
      '/' +
      date.getUTCFullYear() +
      ' @' +
      date.getUTCHours() +
      ':' +
      date.getUTCMinutes() +
      ':' +
      date.getUTCSeconds() +
      ' UTC'
    );
  }
  let friendlyEndTime: string = formatUTCDate(new Date(challenge.endTime));
  let friendlyStartTime: string = formatUTCDate(new Date(challenge.startTime));

  const recalculateTime = () => {
    let endTime = new Date(challenge.endTime).getTime();
    if (new Date(challenge.startTime) > new Date()) {
      endTime = new Date(challenge.startTime).getTime();
    }
    const now = new Date().getTime();
    const diffMs = endTime - now;
    if (diffMs > 10) {
      timeRemaining = prettyMilliseconds(diffMs, { secondsDecimalDigits: 0 });
    }
  };

  onMount(() => {
    determineGalleryCover();
    recalculateTime();
    // run on an interval
    const interval = setInterval(() => {
      recalculateTime();
    }, 1000);
    return () => clearInterval(interval);
  });

  function handleClick() {
    if (!challenge) return;
    if (new Date(challenge.startTime) > new Date()) return;

    if (selectable) {
      selected = !selected;
      return;
    }

    goto('/challenge/' + challenge.challengeTitle);
  }
</script>

{#if variant === 'horizontal'}
  <button
    on:click={handleClick}
    on:keydown={handleClick}
    class:bg-[linear-gradient(360deg,_#b5e9d1_0%,#ffffff_20%,#ffffff_50%)]={isBefore(
      challenge.endTime
    ) && new Date(challenge.startTime) < new Date()}
    class="{containerCls} wrelative flex rounded-xl outline outline-1 shadow-md bg-white outline-garden-grey-300 w-full h-48 select-none cursor-pointer"
    in:fade|global={{ duration: disableTransitions ? 0 : 400 }}
  >
    {#if selectable}
      <div
        class="absolute top-2 left-2 rounded-full w-7 h-7 border-4 grid place-items-center {selected
          ? 'border-white bg-brand-green'
          : 'border-grey-neutral-30 border-opacity-80 bg-grey-transparency-dark bg-opacity-60'} "
      >
        {#if selected}
          <CheckIcon class="stroke-white" size="18" />
        {/if}
      </div>
    {/if}

    <div class="bg-gray-100 w-[45%] rounded-l-xl overflow-hidden">
      {#if displayGalleryCover}
        <Media class=" object-cover w-full h-full" src={displayGalleryCover} alt="Gallery Cover" />
      {/if}
    </div>

    <div class="px-4 py-6 flex flex-col gap-2">
      <h3 class="text-grey-transparency-dark font-medium" title={challenge?.challengeTitle}>
        {challenge?.challengeTitle}
      </h3>
      <h4 class="text-grey-transparency-dark font-sm" title={challenge?.challengeDescription}>
        {challenge?.challengeDescription}
      </h4>

      <div class="flex gap-1 text-grey-transparency-dark text-opacity-60 text-sm">
        <span>{challenge?.challengePosts.length} posts</span>
      </div>
    </div>
  </button>
{:else if variant === 'vertical'}
  <button
    on:click={handleClick}
    on:keydown={handleClick}
    class:bg-[linear-gradient(360deg,_#b5e9d1_0%,#ffffff_20%,#ffffff_50%)]={isBefore(
      challenge.endTime
    ) && new Date(challenge.startTime) < new Date()}
    class="{containerCls} relative flex flex-col bg-white rounded-xl shadow --updated-border-outline w-full h-[392px] select-none cursor-pointer"
    in:fade|global={{ duration: disableTransitions ? 0 : 400 }}
  >
    {#if selectable}
      <div
        class="absolute top-2 left-2 rounded-full w-7 h-7 border-4 grid place-items-center {selected
          ? 'border-white bg-[#b5e9d1]'
          : 'border-grey-neutral-30 border-opacity-80 bg-grey-transparency-dark bg-opacity-60'} "
      >
        {#if selected}
          <CheckIcon class="stroke-white" size="18" />
        {/if}
      </div>
    {/if}

    <div class="bg-gray-100 relative w-full rounded-t-xl overflow-hidden h-[70%]">
      <!-- {#if timeRemaining}
        <span
          class=" absolute text-sm bottom-2 right-3 border border-brand-green-10 text-brand-green rounded-full px-2 whitespace-nowrap font-medium
          "
          class:bg-brand-green-10={new Date(challenge.startTime) < new Date()}
          class:bg-white={new Date(challenge.startTime) > new Date()}
        >
          {#if new Date(challenge.startTime) > new Date()}
            Starts in
          {/if}
          {timeRemaining ?? ''}
        </span>
        <span class="text-xs font-medium text-brand-green-90 opacity-60">
          {#if new Date(challenge.startTime) < new Date()}
            Ends on {friendlyEndTime}
          {:else}
            Starts on {friendlyStartTime}
          {/if}
        </span>
      {/if} -->
      {#if displayGalleryCover}
        <Media class="object-cover w-full h-full" src={displayGalleryCover} alt="Gallery Cover" />
      {/if}
    </div>

    <div class="w-full px-4 py-6 flex flex-col gap-2">
      <h3
        class="text-grey-transparency-dark font-medium empty:bg-gray-100 h-6 rounded empty:w-32"
        title={challenge?.challengeTitle || ''}
      >
        {challenge?.challengeTitle || ''}
      </h3>

      <h4
        class="text-grey-transparency-dark text-xs font-small empty:bg-gray-100 h-6 rounded empty:w-32"
        title={challenge.challengeDescription}
      >
        {`${challenge.challengeDescription.slice(0, 85)}${
          challenge.challengeDescription.length === 85 ? '...' : ''
        }`}
      </h4>

      <div class="flex items-center justify-between">
        <div class="flex flex-wrap gap-1 text-grey-transparency-dark text-opacity-60 text-sm">
          <div class="empty:bg-gray-100 h-5 rounded empty:w-8">
            {challenge?.challengePosts?.length || 0}
          </div>
          posts
        </div>

        {#if isBefore(challenge.endTime) && new Date(challenge.startTime) < new Date()}
          <div class="text-sm">
            <DefaultTag sm highContrast rounded green clickable={false} selected={true}>
              Active
            </DefaultTag>
          </div>
        {/if}
      </div>
    </div>
  </button>
{/if}
